import { html, esc, raw } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { search as runSearch } from '../lib/content.js';
import { topbar } from '../components/chrome.js';
import { deva } from './people.js';

function highlight(text, q) {
  const terms = q.toLowerCase().split(/\s+/).filter((t) => t.length > 1).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!terms.length) return esc(text);
  const re = new RegExp(`(${terms.join('|')})`, 'ig');
  return String(text).split(re).map((part, i) => (i % 2 ? `<mark>${esc(part)}</mark>` : esc(part))).join('');
}

function clip(text, q, max = 220) {
  const s = String(text);
  if (s.length <= max) return s;
  const t = q.toLowerCase().split(/\s+/).find((x) => x.length > 1) || '';
  const i = t ? s.toLowerCase().indexOf(t) : -1;
  if (i < 80) return `${s.slice(0, max).replace(/\s+\S*$/, '')}…`;
  const start = s.lastIndexOf(' ', i - 60);
  return `…${s.slice(start + 1, start + 1 + max).replace(/\s+\S*$/, '')}…`;
}

function results(r, q) {
  const total = r.verses.length + r.concepts.length + r.people.length + r.lessons.length;
  if (!total) {
    return html`<div class="empty"><span class="empty__icon">${icon('search', { size: 24 })}</span>
      <p class="empty__title">Nothing found for "${q}"</p>
      <p class="empty__text">Try a simpler word (duty, fear, mind), a name (Arjuna) or a verse number like 2.47.</p></div>`;
  }
  return html`
    ${r.concepts.length ? html`<section class="search__group" aria-labelledby="sr-ideas"><h2 class="eyebrow" id="sr-ideas">Ideas</h2>
      <ul class="list">${r.concepts.map((g) => html`<li><a class="list__item" href="/concept/${g.id}">
        <span class="concept-list__sa deva" lang="sa" aria-hidden="true">${g.sa}</span>
        <span class="list__body"><span class="list__title">${raw(highlight(g.term, q))}</span><span class="list__sub">${raw(highlight(g.short, q))}</span></span>
        <span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>`)}</ul></section>` : ''}
    ${r.people.length ? html`<section class="search__group" aria-labelledby="sr-people"><h2 class="eyebrow" id="sr-people">People</h2>
      <ul class="list">${r.people.map((p) => html`<li><a class="list__item" href="/person/${p.id}">
        <span class="avatar${p.id === 'krishna' ? ' avatar--krishna' : ''}" aria-hidden="true">${deva(p)}</span>
        <span class="list__body"><span class="list__title">${raw(highlight(p.name, q))}</span><span class="list__sub">${p.role}</span></span>
        <span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>`)}</ul></section>` : ''}
    ${r.lessons.length ? html`<section class="search__group" aria-labelledby="sr-lessons"><h2 class="eyebrow" id="sr-lessons">Lessons</h2>
      <ul class="list">${r.lessons.map((l) => html`<li><a class="list__item" href="/lesson/${l.id}">
        <span class="list__body"><span class="list__title">${raw(highlight(l.title, q))}</span><span class="list__sub">${l.ch === 0 ? 'Background' : `Chapter ${l.ch}, lesson ${l.n}`} · ${l.idea}</span></span>
        <span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>`)}</ul></section>` : ''}
    ${r.verses.length ? html`<section class="search__group" aria-labelledby="sr-verses"><h2 class="eyebrow" id="sr-verses">Verses <span class="muted">(${r.verses.length}${r.verses.length === 40 ? '+' : ''})</span></h2>
      <div class="verse-rows">${r.verses.map((x) => html`<a class="verse-row" href="/verse/${x.ch}/${x.v}">
        <span class="verse-row__ref num">${x.id}</span>
        <span class="verse-row__body"><span class="verse-row__text">${raw(highlight(clip(x.verse.translation, q), q))}</span></span>
        ${icon('chevron-right', { size: 18, className: 'verse-row__chev' })}</a>`)}</div></section>` : ''}`;
}

export default async function searchView(params, r) {
  const initial = (r.query && r.query.get('q')) || '';
  return {
    title: 'Search',
    html: html`<div class="search">
      ${topbar({ back: '/explore', title: 'Search', search: false })}
      <header class="wrap page-head">
        <h1 class="h1">Search</h1>
        <form class="search-field search__field" role="search" data-form>
          ${icon('search', { size: 20 })}
          <label class="sr-only" for="q">Search verses, ideas and people</label>
          <input class="input" id="q" name="q" type="search" value="${initial}" placeholder="Try: duty, fear, 2.47, Arjuna" autocomplete="off" enterkeyhint="search" spellcheck="false">
        </form>
      </header>
      <div class="wrap search__results" data-results aria-live="polite"></div>
    </div>`,
    mount(root) {
      const input = root.querySelector('#q');
      const out = root.querySelector('[data-results]');
      let timer = null;
      let seq = 0;
      const suggestions = html`<div class="search__hints"><p class="eyebrow">Try</p><div class="chips">
        ${['duty', 'fear', 'desire', 'the Self', 'meditation', 'Arjuna', '2.47', 'death'].map((t) => html`<button type="button" class="chip" data-q="${t}">${t}</button>`)}
      </div></div>`;
      const runQuery = async (q) => {
        const my = ++seq;
        try {
          history.replaceState(history.state, '', q ? `/search?q=${encodeURIComponent(q)}` : '/search');
        } catch {
          // ignore
        }
        if (q.trim().length < 2) {
          out.innerHTML = String(suggestions);
          return;
        }
        out.innerHTML = String(html`<p class="muted search__loading">Searching all 700 verses…</p>`);
        try {
          const r = await runSearch(q);
          if (my === seq) out.innerHTML = String(results(r, q.trim()));
        } catch {
          if (my === seq) out.innerHTML = String(html`<div class="empty"><p class="empty__title">Search needs the full text</p><p class="empty__text">Reconnect to the internet once, and search will then work offline too.</p></div>`);
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => runQuery(input.value), 180);
      });
      root.querySelector('[data-form]').addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(timer);
        runQuery(input.value);
        input.blur();
      });
      out.addEventListener('click', (e) => {
        const b = e.target.closest('[data-q]');
        if (b) {
          input.value = b.dataset.q;
          runQuery(b.dataset.q);
        }
      });
      runQuery(initial);
      if (!initial) setTimeout(() => input.focus(), 60);
      return () => clearTimeout(timer);
    },
  };
}
